<!-- 
  A wrapper component for cv-button which extends its basic functionality to include state transitions
-->
<template>
  <div class="fetch-data-button button" :class="classes" @click="handleClick">
    <cv-button
      type="button"
      :kind="kind"
      :size="size"
      :disabled="disabled"
      :class="{ 'bx--btn--icon-only': iconOnly }"
    >
      <Cross24 v-if="dataError" class="sls-icon" />
      <!-- Use v-show instead of v-if because the popper reference needs to be instantiated in the DOM -->
      <message-component v-show="showErrorMessage" ref="message">
        {{ computedErrorMessage }}
      </message-component>

      <template v-if="dataCompleted && !dataLoading">
        <svg-icon :name="checkmarkIconSize" kind="action" />
      </template>
      <!-- 3 dots for the loading animation -->
      <div v-if="dataLoading" class="dots">
        <span class="dot" />
        <span class="dot" />
        <span class="dot" />
      </div>
      <!-- Button text will be rendered in default slot -->
      <slot />
    </cv-button>
  </div>
</template>

<script setup lang="ts">
import { createPopper } from "@popperjs/core";
import { ref, computed, watch, nextTick, useTemplateRef, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";
import Cross24 from "@images/icons/action/24px/Cross24.svg?component";

const { t } = useI18n();
const { vm } = useVm();

// Props
interface Props {
  kind?: string;
  size?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  error?: boolean;
  loading?: boolean;
  completed?: boolean;
  tipPosition?: string;
  tipAlignment?: string;
  disablePopper?: boolean;
}

const {
  kind = "primary",
  size = "",
  iconOnly = false,
  disabled = false,
  errorMessage = "",
  error = false,
  loading = false,
  completed = false,
  tipPosition = "bottom",
  tipAlignment = "",
  disablePopper = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  click: [data: unknown];
  resetErrorState: [];
  resetCompletedState: [];
}>();

const VALID_TIP_POSITIONS = ["top", "right", "bottom", "left"];
const VALID_TIP_ALIGNMENTS = ["", "start", "end"];

// Data
const dataLoading = ref(loading);
const dataCompleted = ref(completed);
const dataError = ref(error);
const popperInstance = ref(null);
const showErrorMessage = ref(false);
const transitionDuration = ref(3000);

// Template Refs
const message = useTemplateRef<HTMLElement>("message");

// Computed
const computedErrorMessage = computed(() => {
  return errorMessage || t("fetch_data_button_error_message");
});

const classes = computed(() => {
  const classesObj = {
    _loading: dataLoading.value,
    _error: dataError.value,
    _completed: dataCompleted.value,
    "icon-only": iconOnly,
  };

  const tipClasses =
    tipAlignment !== "" ? `tip-${tipPosition} tip-${tipAlignment}` : `tip-${tipPosition}`;

  return [classesObj, tipClasses];
});

const checkmarkIconSize = computed(() => {
  return size === "small" ? "Checkmark16" : "Checkmark24";
});

// Methods
const handleClick = (value) => {
  if (!disabled && !completed && !error) {
    emit("click", value);
  }
};

const validateTipPosition = (value) => {
  return VALID_TIP_POSITIONS.includes(value);
};

const validateTipAlignment = (value) => {
  return VALID_TIP_ALIGNMENTS.includes(value);
};

// Watch
watch(
  () => error,
  () => {
    dataError.value = error;
    if (dataError.value) {
      if (computedErrorMessage.value && computedErrorMessage.value.length > 0 && !disablePopper) {
        showErrorMessage.value = true;
        nextTick(() => {
          const popperProps = {
            placement: tipAlignment !== "" ? `${tipPosition}-${tipAlignment}` : tipPosition,
          };
          popperProps.modifiers = [
            {
              name: "flip",
              options: {
                allowedAutoPlacements: ["bottom"],
              },
            },
          ];
          popperInstance.value = createPopper(vm.$el, message.value.$el, popperProps);
        });
      }

      setTimeout(() => {
        emit("resetErrorState");
        showErrorMessage.value = false;
      }, transitionDuration.value);
    }
  },
);

watch(
  () => loading,
  () => {
    dataLoading.value = loading;
  },
);

watch(
  () => completed,
  () => {
    dataCompleted.value = completed;
    if (dataCompleted.value) {
      setTimeout(() => {
        emit("resetCompletedState");
      }, transitionDuration.value);
    }
  },
);

watch(
  () => tipPosition,
  (newVal) => {
    validateTipPosition(newVal);
  },
);

watch(
  () => tipAlignment,
  (newVal) => {
    validateTipAlignment(newVal);
  },
);

onBeforeUnmount(() => {
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.fetch-data-button {
  display: inline-flex;
  flex: 0 1 auto;
  position: relative;
  max-width: 20rem;

  %tooltip-top {
    top: 100%;
    border-width: 12px;
    border-color: $inverse-02 transparent transparent transparent;
  }

  &:is(._loading, ._error, ._completed) {
    .bx--btn {
      &:focus {
        box-shadow: none;
      }
    }
  }

  &._loading {
    &.icon-only {
      width: 40px;
      height: 40px;
    }

    // Default dynamic color variables
    --loading-primary: #{$interactive-01};
    --loading-primary-tinted: #{tint($interactive-01, 50%)};

    .bx--btn {
      background-color: $ui-01 !important;
      color: $ui-01;
      border: 3px solid var(--loading-primary);
      pointer-events: none;
      user-select: none;
      animation: loading-border 2s ease-in-out infinite;

      // Use visibility hidden so that the length of the button is retained and it doesn't jump
      > span {
        visibility: hidden;
      }
    }

    .dots {
      display: flex;
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      ~ svg,
      ~ button {
        visibility: hidden;
      }
    }

    .dot {
      height: 0.5rem;
      width: 0.5rem;
      background-color: var(--loading-primary);
      border-radius: 100%;
      margin: $spacing-02;
      animation: loading-dots 2s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.4s;
      }

      &:nth-child(3) {
        animation-delay: 0.8s;
      }
    }

    @keyframes loading-dots {
      50% {
        opacity: 0;
        transform: scale(0.7) translateY(10px);
      }
    }

    @keyframes loading-border {
      0% {
        border-color: var(--loading-primary-tinted) var(--loading-primary-tinted)
          var(--loading-primary) var(--loading-primary);
      }
      50% {
        border-color: var(--loading-primary) var(--loading-primary) var(--loading-primary-tinted)
          var(--loading-primary-tinted);
      }
      100% {
        border-color: var(--loading-primary-tinted) var(--loading-primary-tinted)
          var(--loading-primary) var(--loading-primary);
      }
    }
  }

  &._error,
  &._completed {
    .bx--btn {
      pointer-events: none;
    }

    .bx--btn--icon-only {
      width: 2.5rem;
      height: 2.5rem;
    }

    svg {
      margin-right: $spacing-02;

      [stroke] {
        stroke: currentColor;
      }

      [fill] {
        fill: currentColor;
      }

      ~ svg,
      ~ button {
        display: none;
      }
    }
  }

  &._completed {
    .bx--btn {
      background-color: tint($interactive-01, 80%);
      border: 2px solid $interactive-01;
      color: $interactive-01;
    }
  }

  &.button._error {
    .bx--btn,
    .bx--btn.bx--btn--icon-only {
      background-color: tint($support-01, 95%);
      border: 1px solid tint($support-01, 40%);
      color: $support-01;

      .sls-icon {
        [fill] {
          fill: $support-01;
        }
      }
    }

    .bx--btn--icon-only {
      .sls-icon {
        margin-left: -1px; // adjust icon to center due to 1px border
      }
    }

    .message {
      position: absolute;
      justify-content: center;
      color: $inverse-01;
      background-color: $inverse-02;
      border-radius: 10px;
      text-transform: none;
      min-width: 15rem;
      padding: $spacing-03 $spacing-05;
      z-index: z-map("button-tooltip");

      &:before {
        content: "";
        position: absolute;
        top: -0.75rem;
        left: 50%;
        border-width: 0px 12px 12px 12px;
        border-style: solid;
        border-color: transparent transparent $inverse-02 transparent;
        transform: translateX(-50%);
      }

      &[data-popper-placement="top"] {
        &:before {
          @extend %tooltip-top;
        }
      }

      span {
        @include carbon--type-style("label-01", true);
      }
    }

    &.tip-top {
      .message {
        &:before {
          @extend %tooltip-top;
        }
      }
    }

    &.tip-left {
      .message {
        width: 12rem;
        min-width: 12rem;

        &:before {
          top: 50%;
          left: 98%;
          border-width: 12px;
          border-color: transparent transparent transparent $inverse-02;
          transform: translateY(-50%);
        }
      }
    }

    &.tip-right {
      .message {
        width: 12rem;
        min-width: 12rem;

        &:before {
          top: 50%;
          left: -1.25rem;
          border-width: 12px;
          border-color: transparent $inverse-02 transparent transparent;
          transform: translateY(-50%);
        }
      }
    }

    &.tip-start {
      .message {
        &:before {
          left: 20%;
        }
      }
    }

    &.tip-end {
      .message {
        &:before {
          left: 80%;
        }
      }
    }
  }

  &.icon-only {
    width: 40px;
    height: 40px;

    &._completed,
    &._error {
      svg {
        margin-right: 0;

        ~ svg,
        ~ button {
          display: none;
        }
      }
    }

    &._completed {
      .bx--btn.bx--btn--icon-only {
        color: $interactive-01;
      }

      path {
        fill: none;
      }
    }

    &._loading {
      .dot {
        height: 0.25rem;
        width: 0.25rem;
        margin: $spacing-01;
      }

      .bx--btn.bx--btn--icon-only {
        border: 1px solid $interactive-01;
      }
    }

    .bx--btn {
      width: 40px;
      height: 40px;
      padding: 0;

      // To override Carbon styling for hover/focus state and tooltip fill for the svg,
      // as the original svg icon fill is none when imported from Figma
      &.bx--btn--icon-only {
        &:hover,
        &:focus {
          > svg {
            fill: none;
          }
        }

        > .bx--tooltip__trigger {
          > svg {
            fill: none;
          }
        }
      }
    }
  }

  button {
    &[disabled] {
      opacity: 1;
    }
  }

  .start-chat {
    span {
      text-transform: none;
    }
  }
}
</style>
